import { Component, Input, OnInit } from '@angular/core';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { animate, style, transition, trigger } from '@angular/animations';
import { ApiService } from '@app/Services/APIService';
import { NotifyService } from '@app/Services/NotifyService';
import { Router } from '@angular/router';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { InputDataUnit } from '@app/Global/Models/ClientModels';
import { Tooltip } from 'chart.js';

@Component({
    selector: 'ApplicationDetail',
    templateUrl: './ApplicationDetail.html',
    styleUrls: ['./ApplicationDetail.scss'],
    animations: [
        trigger('fadeInSection', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('0.3s ease-in', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class ApplicationDetail implements OnInit {

    // Modal identifier for minimizing/maximizing
    @Input() ModalIdentifier: string;    // Form Title
    public PageTitle: string = "Loan Application Form";    // New ModelData that captures form fields in an array
    public ModelData: InputDataUnit[] = [];
    public LoanDetailInputData: any[] = [];
    public ApplicantInputData: any[] = [];

    // Track active tab - default to Loan Details tab
    public ActiveTab: string = 'LoanDetails';

    // Global lender selection (moved outside individual loan details)
    public selectedLender: string = '';

    // Loan Type Options for dropdown
    public LoanTypeOptions = [
        { ControlGUID: "Residential", ControlValue: "Residential" },
        { ControlGUID: "Commercial", ControlValue: "Commercial" },
        { ControlGUID: "Lease", ControlValue: "Lease" },
        { ControlGUID: "EmployeeShare", ControlValue: "Employee Share" }
    ];

    // Loan Term Options (12-360 months)
    public LoanTermOptions = [];    // Loan Purpose Options
    public LoanPurposeOptions = [
        { ControlGUID: "Purchase", ControlValue: "Purchase" },
        { ControlGUID: "Refinance", ControlValue: "Refinance" },
        { ControlGUID: "Renovation", ControlValue: "Renovation" },
        { ControlGUID: "Investment", ControlValue: "Investment" },
        { ControlGUID: "DebtConsolidation", ControlValue: "Debt Consolidation" },
        { ControlGUID: "Other", ControlValue: "Other" }
    ];

    // Lender Options
    public LenderOptions = [
        { ControlGUID: "TestLenderA", ControlValue: "Test Lender A" },
        { ControlGUID: "TestLenderB", ControlValue: "Test Lender B" }
    ];

    public GenderOptions = [
        { ControlGUID: "Male", ControlValue: "Male" },
        { ControlGUID: "Female", ControlValue: "Female" }
    ];

    // Product Options
    public ProductOptions = [
        { ControlGUID: "TLA_PI", ControlValue: "TLA P & I" },
        { ControlGUID: "TLA_IO", ControlValue: "TLA IO" }
    ];

    // Show mailing address input if different from residential address
    public ShowMailingAddress: boolean = false;

    // Max date for date picker (ensure applicant is at least 18 years old)    public DTPMaxDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18));    // Added properties for new loan origination form template - now supports multiple loan details
    //public LoanDetails: ILoanDetails[] = [];
    public LoanDetails: any[] = [];
    public ApplicantDetails: IApplicantDetail[] = [];

    public LoanTypeSelectionOptions: string[] = ['Business Loan', 'Construction Loan', 'Secured Loan'];
    public LoanPurposeSelectionOptions: string[] = ['Business Acquisition', 'Investment', 'Construction'];
    public RepaymentMethodOptions: string[] = ['Direct Debit', 'Prepaid Interest', 'NA - Capitalising'];
    public PaymentTypeOptions: string[] = ['Weekly', 'Fortnightly', 'Monthly'];

    public ApplicantTypeOptions: string[] = ['Individual', 'Company', 'Trust'];
    public ApplicantRoleOptions: string[] = ['Primary Borrower', 'Secondary Borrower', 'Guarantor', 'Correspondent', 'Sales Consultant'];


    public Securities: ISecurity[] = [];
    public AustralianStates: string[] = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

    public BankDetails: IBankDetails = { bsb: '', accountNumber: '', accountName: '' };

    // Constructor
    constructor(
        public globalFunctions: GlobalFunctions,
        private apiService: ApiService,
        private notifyService: NotifyService,
        private router: Router,
        private clientDataStore: ClientDataStore,
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<ApplicationDetail>
    ) { }

    // Initialize the component
    ngOnInit(): void {
        // Initialize loan term options from 12 to 360 months
        for (let i = 12; i <= 360; i += 12) {
            this.LoanTermOptions.push({ ControlGUID: i.toString(), ControlValue: i.toString() + " months" });
        }

        // Initialize the ModelData with form fields
        this.ModelData_Initialize();

        // Initialize with one loan detail entry
        this.LoanDetail_Add();

        // Initialize with one borrower
        this.Applicant_Add();

        // Set the section title for primary borrower
        if (this.ApplicantDetails.length > 0) {
            this.ApplicantDetails[0].sectionTitle = 'Primary Borrower';
            this.ApplicantDetails[0].role = 'Primary Borrower';
            this.ApplicantDetails[0].type = 'Individual';
        }
    }// Initialize ModelData with form fields
    private ModelData_Initialize(): void {        // A. Loan Details Section
        this.LoanDetailInputData.push(
            // Note: Lender dropdown removed as it's now handled at the global level outside individual loan details
            { Entity: "LoanDetails", Name: "INP_Product", DisplayName: "Product Type", Placeholder: "Choose a Product Type", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "LenderProducts", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "ProductOptions" },
            { Entity: "LoanDetails", Name: "INP_UniqueLoanID", DisplayName: "Unique Loan ID", Placeholder: "Unique Loan ID", HTMLInputType: "hidden", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: this.globalFunctions.GenerateFastGUID(), IsInputField: false },
            { Entity: "LoanDetails", Name: "INP_IsPrimary", DisplayName: "Is Primary Loan", Placeholder: "Is Primary Loan?", HTMLInputType: "checkbox", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: true, IsInputField: false },
            { Entity: "LoanDetails", Name: "INP_LoanTitleLabel", DisplayName: "Loan Title Label", Placeholder: "", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", IsInputField: false },
            { Entity: "LoanDetails", Name: "INP_OriginatorRefNumber", DisplayName: "Lender Reference No", Placeholder: "Lender Reference No", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", TooltipDisplayText: "Name of the Loan Opportunity", ShowToolTip: false },
            { Entity: "LoanDetails", Name: "INP_LoanAmount", DisplayName: "Current Approved Amount", Placeholder: "Enter the current approved amount", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", ToolTipDisplayText: "The approved amount at the loan level", ShowToolTip: false },
            { Entity: "LoanDetails", Name: "INP_LoanPurpose", DisplayName: "Loan Purpose", Placeholder: "Select loan purpose", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", DropdownOptions: "LoanPurposeSelectionOptions", CustomCSS: "col-4", IsMandatory: true, Value: "" },
            { Entity: "LoanDetails", Name: "INP_LoanType", DisplayName: "Loan Type", Placeholder: "Select loan type", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", DropdownOptions: "LoanTypeSelectionOptions", CustomCSS: "col-4", IsMandatory: true, Value: "" },
            { Entity: "LoanDetails", Name: "INP_LoanTerm", DisplayName: "Loan Term (Months)", Placeholder: "Enter loan term", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "" },
            { Entity: "LoanDetails", Name: "INP_EstimatedSettlementDate", DisplayName: "Estimated Settlement Date", Placeholder: "Select estimated settlement date", HTMLInputType: "date", Type: "shortdate", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "" },
            { Entity: "LoanDetails", Name: "INP_InterestRate", DisplayName: "Interest Rate", Placeholder: "Interest Rate %", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "" },
            { Entity: "LoanDetails", Name: "INP_InterestType", DisplayName: "Interest Type", Placeholder: "Select interest type", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", DropdownOptions: "", CustomCSS: "col-4", IsMandatory: true, Value: "" },
            { Entity: "LoanDetails", Name: "INP_InterestRateMargin", DisplayName: "Interest Margin", Placeholder: "Interest Margin %", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "" },
            { Entity: "LoanDetails", Name: "INP_DefaultMargin", DisplayName: "Default Margin", Placeholder: "Default Margin %", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "" },
            { Entity: "LoanDetails", Name: "INP_RepaymentMethod", DisplayName: "Repayment Method", Placeholder: "Select repayment method", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", DropdownOptions: "RepaymentMethodOptions", CustomCSS: "col-4", IsMandatory: false, Value: "Monthly" },
            { Entity: "LoanDetails", Name: "INP_PrepaidInterestAccount", DisplayName: "Prepaid Interest Account Required?", Placeholder: "", HTMLInputType: "checkbox", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: false },
            { Entity: "LoanDetails", Name: "INP_RetentionAccount", DisplayName: "Retention Account Required?", Placeholder: "", HTMLInputType: "checkbox", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: false },


            // { Name: "INP_LoanLVR", DisplayName: "LVR %", Placeholder: "Loan LVR %", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "" },
            // { Name: "INP_BalloonRepaymentAmount", DisplayName: "Balloon Repayment Amount", Placeholder: "Balloon Repayment Amount", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "" },
            // { Name: "INP_ApprovedDate", DisplayName: "Approved Date", Placeholder: "Select approved date", HTMLInputType: "date", Type: "shortdate", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "" },


            // { Name: "INP_PaymentType", DisplayName: "Payment Frequency", Placeholder: "Select payment frequency", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", DropdownOptions: "PaymentTypeOptions", CustomCSS: "col-4", IsMandatory: false, Value: "Monthly" },
            // { Name: "INP_EstimatedRepaymentAmount", DisplayName: "Estimated Repayment", Placeholder: "Calculated repayment", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: false, Value: "", IsReadOnly: true }
        );

        this.ApplicantInputData.push(

            { Entity: "ClientIndividual", Name: "INP_UniqueLoanID", DisplayName: "Unique Loan ID", Placeholder: "Unique Loan ID", HTMLInputType: "hidden", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: this.globalFunctions.GenerateFastGUID(), IsInputField: false },
            { Entity: "ClientIndividual", Name: "INP_ApplicantType", DisplayName: "Applicant Type", Placeholder: "Selet an Applicant Type", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "ApplicantTypeOptions" },
            { Entity: "ClientIndividual", Name: "INP_ApplicantRole", DisplayName: "Applicant Role", Placeholder: "Select an Applicant Role", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "ApplicantRoleOptions" },
            { Entity: "ClientIndividual", Name: "INP_FirstName", DisplayName: "First Name", Placeholder: "Enter First Name", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_MiddleName", DisplayName: "Middle Name", Placeholder: "Enter Middle Name", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_LastName", DisplayName: "Last Name", Placeholder: "Enter Last Name", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_DOB", DisplayName: "Date of Birth", Placeholder: "Enter DOB", HTMLInputType: "datetime", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_Email", DisplayName: "Email", Placeholder: "Enter Email", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_Mobile", DisplayName: "Mobile", Placeholder: "Enter Mobile", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientIndividual", Name: "INP_Gender", DisplayName: "Gender", Placeholder: "Select a Gender", HTMLInputType: "dropdown", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "GenderOptions" },

            { Entity: "ClientCompany", Name: "INP_CompanyName", DisplayName: "Company Name", Placeholder: "Enter Company Name", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientCompany", Name: "INP_TradingAs", DisplayName: "Trading As", Placeholder: "Enter Trading As", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientCompany", Name: "INP_TrusteeName", DisplayName: "Trustee Name", Placeholder: "Enter Trustee Name", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientCompany", Name: "INP_ACN", DisplayName: "ACN", Placeholder: "Enter ACN", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
            { Entity: "ClientCompany", Name: "INP_ABN", DisplayName: "ABN", Placeholder: "Enter ABN", HTMLInputType: "textarea", Type: "string", AutoCompleteControlTypeName: "", CustomCSS: "col-4", IsMandatory: true, Value: "", DropdownOptions: "" },
        );

        this.ModelData.push(
            new InputDataUnit({
                Name: "INP_Lender",
                DisplayName: "Lender",
                Placeholder: "Select lender",
                HTMLInputType: "dropdown",
                Type: "string"
            }),
            new InputDataUnit({
                Name: "INP_Product",
                DisplayName: "Product",
                Placeholder: "Select product",
                HTMLInputType: "dropdown",
                Type: "string"
            }),
            new InputDataUnit({
                Name: "INP_OriginatorRefID",
                DisplayName: "Originator Ref ID",
                Placeholder: "Enter reference ID",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 1,
                MaxLength: 50
            }),
            new InputDataUnit({
                Name: "INP_LoanType",
                DisplayName: "Loan Type",
                Placeholder: "Select loan type",
                HTMLInputType: "dropdown",
                Type: "string"
            }),
            new InputDataUnit({
                Name: "INP_RequestedLoanAmount",
                DisplayName: "Requested Loan Amount",
                Placeholder: "Enter loan amount",
                HTMLInputType: "textarea",
                Type: "currency",
                MinValue: this.globalFunctions.TextAreaValidMinNumeric,
                MaxValue: this.globalFunctions.TextAreaValidMaxNumeric
            }),
            new InputDataUnit({
                Name: "INP_LoanTerm",
                DisplayName: "Loan Term",
                Placeholder: "Select loan term",
                HTMLInputType: "dropdown",
                Type: "string"
            }),
            new InputDataUnit({
                Name: "INP_LoanPurpose",
                DisplayName: "Loan Purpose",
                Placeholder: "Select or enter loan purpose",
                HTMLInputType: "dropdown",
                Type: "string"
            })
        );

        // B. Applicant Information Section
        this.ModelData.push(
            new InputDataUnit({
                Name: "INP_FullName",
                DisplayName: "Full Name",
                Placeholder: "Enter full name",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 1,
                MaxLength: 100
            }),
            new InputDataUnit({
                Name: "INP_DateOfBirth",
                DisplayName: "Date of Birth",
                Placeholder: "Select date of birth",
                HTMLInputType: "date",
                Type: "shortdate"
            }),
            new InputDataUnit({
                Name: "INP_NationalID",
                DisplayName: "National ID / SSN",
                Placeholder: "Enter national ID or SSN",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 1,
                MaxLength: 20
            }),
            new InputDataUnit({
                Name: "INP_PhoneNumber",
                DisplayName: "Phone Number",
                Placeholder: "Enter phone number",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 10,
                MaxLength: 15
            }),
            new InputDataUnit({
                Name: "INP_Email",
                DisplayName: "Email Address",
                Placeholder: "Enter email address",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 5,
                MaxLength: 100
            }),
            new InputDataUnit({
                Name: "INP_ResidentialAddress",
                DisplayName: "Residential Address",
                Placeholder: "Enter residential address",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 1,
                MaxLength: 200
            }),
            new InputDataUnit({
                Name: "INP_DifferentMailingAddress",
                DisplayName: "Mailing Address different from Residential",
                LabelUnchecked: "Click if mailing address is different",
                LabelChecked: "Mailing address is different",
                LabelDisplay: "Different Mailing Address",
                HTMLInputType: "checkbox",
                Type: "string",
                IsChecked: false
            }),
            new InputDataUnit({
                Name: "INP_MailingAddress",
                DisplayName: "Mailing Address",
                Placeholder: "Enter mailing address",
                HTMLInputType: "textarea",
                Type: "string",
                MinLength: 1,
                MaxLength: 200
            })
        );
    }

    // Get the matching model data input, used for form controls
    public ModelDataUnit_Get(input) {
        const modelDataItem = this.ModelData.filter(x => x.Name === input.id)[0];

        if (!this.globalFunctions.isEmpty(modelDataItem)) {
            return modelDataItem;
        }
    }

    // Sync the display of UI input elements
    public InputView_Sync(): void {
        // A small delay so that user click still catches the correct UI element
        this.globalFunctions.delay(200).then(() => {
            // Handle showing/hiding mailing address based on checkbox
            const differentMailingAddressDU = this.ModelDataUnit_Get({ id: "INP_DifferentMailingAddress" });
            if (!this.globalFunctions.isEmpty(differentMailingAddressDU)) {
                this.ShowMailingAddress = differentMailingAddressDU.IsChecked;

                // Get the mailing address input
                const mailingAddressDU = this.ModelDataUnit_Get({ id: "INP_MailingAddress" });
                if (!this.globalFunctions.isEmpty(mailingAddressDU)) {
                    mailingAddressDU.Disabled = !this.ShowMailingAddress;
                }
            }

        });
    }

    // Method to change active tab
    public Tab_Change(tabName: string): void {
        this.ActiveTab = tabName as 'LoanDetails' | 'Applicants' | 'Securities' | 'BankDetails' | 'Summary';
    }

    // Save the application form data
    public ApplicationForm_Save(): void {
        // Implementation for saving form data would go here
        // Typically would validate fields and send to API service
        this.notifyService.Success_Show("Application submitted successfully", "Success");
    }

    // Helper method to map field names from LoanDetailData to loan object properties
    public Field_GetPropertyName(fieldName: string): string {
        // Map INP_ prefixed field names to loan object properties
        const fieldMap: { [key: string]: string } = {
            'INP_Lender': 'lender',
            'INP_Product': 'product',
            'INP_OriginatorRefNumber': 'lenderRefNumber',
            'INP_LoanAmount': 'loanAmount',
            'INP_LoanLVR': 'lvr',
            'INP_BalloonRepaymentAmount': 'balloonAmount',
            'INP_ApprovedDate': 'approvedDate',
            'INP_EstimatedSettlementDate': 'settlementDate',
            'INP_LoanType': 'loanType',
            'INP_LoanTerm': 'loanTermMonths',
            'INP_PaymentType': 'paymentType',
            'INP_EstimatedRepaymentAmount': 'estimatedRepaymentAmount'
        };

        const mappedValue = fieldMap[fieldName];
        return mappedValue !== undefined ? mappedValue : fieldName;
    }

    // Helper method to map field names from LoanDetailData to loan object properties
    public Property_GetName(fieldName: string): string {
        // Map INP_ prefixed field names to loan object properties
        switch (fieldName) {
            case 'INP_Lender': return 'lender';
            case 'INP_Product': return 'product';
            case 'INP_OriginatorRefNumber': return 'lenderRefNumber';
            case 'INP_LoanAmount': return 'loanAmount';
            case 'INP_LoanLVR': return 'lvr';
            case 'INP_BalloonRepaymentAmount': return 'balloonAmount';
            case 'INP_ApprovedDate': return 'approvedDate';
            case 'INP_EstimatedSettlementDate': return 'settlementDate';
            case 'INP_LoanType': return 'loanType';
            case 'INP_LoanTerm': return 'loanTermMonths';
            case 'INP_PaymentType': return 'paymentType';
            case 'INP_EstimatedRepaymentAmount': return 'estimatedRepaymentAmount';
            default: return '';
        }
    }

    // --- Methods for template interactions ---
    public Update_EstimatedRepayment(loanDetail: Array<{ Name: string, Value: string | number | null }>): void {
        try {
            // Find all required fields in the loan detail array
            const loanAmountField = loanDetail.find(f => f.Name === 'INP_LoanAmount');
            const loanTermField = loanDetail.find(f => f.Name === 'INP_LoanTerm');
            const balloonField = loanDetail.find(f => f.Name === 'INP_BalloonRepaymentAmount');
            const paymentTypeField = loanDetail.find(f => f.Name === 'INP_PaymentType');
            const estimatedRepaymentField = loanDetail.find(f => f.Name === 'INP_EstimatedRepaymentAmount');
            const loanTypeField = loanDetail.find(f => f.Name === 'INP_LoanType');

            // Safety checks for required fields
            if (typeof loanAmountField === 'undefined' ||
                typeof loanTermField === 'undefined' ||
                typeof estimatedRepaymentField === 'undefined') {
                console.error('Required loan fields not found');
                return;
            }

            // Validate field values
            const hasValidLoanAmount = loanAmountField.Value !== null && loanAmountField.Value !== '';
            const hasValidLoanTerm = loanTermField.Value !== null && loanTermField.Value !== '';

            if (!hasValidLoanAmount || !hasValidLoanTerm) {
                // Set default value and exit
                estimatedRepaymentField.Value = 0;
                return;
            }

            // Parse numeric values with fallbacks
            const loanAmount = hasValidLoanAmount ? parseFloat(String(loanAmountField.Value)) : 0;
            const loanTerm = hasValidLoanTerm ? parseFloat(String(loanTermField.Value)) : 0;

            // Handle balloon amount if present
            let balloonAmount = 0;
            const hasValidBalloonField = typeof balloonField !== 'undefined' &&
                balloonField.Value !== null &&
                balloonField.Value !== '';

            if (hasValidBalloonField) {
                balloonAmount = parseFloat(String(balloonField.Value));
            }

            // Calculate principal portion (total amount less balloon payment)
            const principalPortion = (loanAmount - balloonAmount);

            // Default periods to loan term (monthly payments)
            let periods = loanTerm;

            // Adjust periods based on payment frequency
            let paymentType = '';
            const hasValidPaymentType = typeof paymentTypeField !== 'undefined' &&
                paymentTypeField.Value !== null &&
                paymentTypeField.Value !== '';

            if (hasValidPaymentType) {
                paymentType = String(paymentTypeField.Value);
            }

            // Apply payment frequency adjustments
            if (paymentType === 'Weekly') {
                periods = loanTerm * 4.345; // Weekly payments (average weeks per month)
            } else if (paymentType === 'Fortnightly') {
                periods = loanTerm * 2.1725; // Fortnightly payments (average per month)
            }

            // Check for interest-only loans
            let loanType = '';
            const hasValidLoanType = typeof loanTypeField !== 'undefined' &&
                loanTypeField.Value !== null &&
                loanTypeField.Value !== '';

            if (hasValidLoanType) {
                loanType = String(loanTypeField.Value);
            }

            // Calculate estimated repayment
            if (loanType === 'Interest Only') {
                // Simple interest-only calculation
                const estimatedInterestRate = 0.05; // 5% p.a. (can be made configurable)
                estimatedRepaymentField.Value = +(loanAmount * estimatedInterestRate / 12).toFixed(2);
            } else {
                // For principal and interest loans
                estimatedRepaymentField.Value = (periods > 0) ? +(principalPortion / periods).toFixed(2) : 0;
            }
        } catch (error) {
            console.error('Error calculating estimated repayment:', error);
        }
    }

    // Get the appropriate loan detail title based on the isPrimary flag
    public LoanDetailTitle_Get(loan: Array<{ Name: string, Value: string | number | boolean | null }>): string {
        if (!this.globalFunctions.isEmpty(loan)) {
            const isPrimaryField = loan.find(x => x.Name === "INP_IsPrimary");
            if (!this.globalFunctions.isEmpty(isPrimaryField) && isPrimaryField.Value === true) {
                return "Primary Loan Details";
            }
        }
        return "Additional Loan Details";
    }



    // Get the appropriate loan detail title based on the isPrimary flag
    public LoanDetailUniqueID_Get(loan: Array<{ Name: string, Value: string | number | boolean | null }>): string {
        if (!this.globalFunctions.isEmpty(loan)) {
            const loanUniqueID = loan.find(x => x.Name === "INP_UniqueLoanID");
            if (!this.globalFunctions.isEmpty(loanUniqueID)) {
                return loanUniqueID.Value as string;
            }
        }
        return "";
    }

    public UniqueID_Get(formDetail: Array<{ Name: string, Value: string | number | boolean | null }>): string {
        if (!this.globalFunctions.isEmpty(formDetail)) {
            const uniqueID = formDetail.find(x => x.Name === "INP_UniqueLoanID");
            if (!this.globalFunctions.isEmpty(uniqueID)) {
                return uniqueID.Value as string;
            }
        }
        return "";
    }

    public LoanDetail_Add(): void {
        const isFirstLoan = this.LoanDetails.length === 0;

        // Create a deep copy of the LoanDetailInputData template
        const loanDetailInputData = JSON.parse(JSON.stringify(this.LoanDetailInputData));

        if (!this.globalFunctions.isEmpty(loanDetailInputData) && loanDetailInputData.length > 0) {
            // Set unique ID for this loan entry
            const uniqueIdField = loanDetailInputData.find(x => x.Name === "INP_UniqueLoanID");
            if (!this.globalFunctions.isEmpty(uniqueIdField)) {
                uniqueIdField.Value = this.globalFunctions.GenerateFastGUID();
            }

            // Set isPrimary flag based on whether it's the first loan
            const isPrimaryField = loanDetailInputData.find(x => x.Name === "INP_IsPrimary");
            if (!this.globalFunctions.isEmpty(isPrimaryField)) {
                isPrimaryField.Value = isFirstLoan;
            }

            // Set the title label (used for display in summary)
            const titleField = loanDetailInputData.find(x => x.Name === "INP_LoanTitleLabel");
            if (!this.globalFunctions.isEmpty(titleField)) {
                titleField.Value = isFirstLoan ? 'Primary Loan Details' : 'Additional Loan Details';
            }
        }

        // Add this loan detail to the list
        this.LoanDetails.push(loanDetailInputData);
    }

    public LoanDetail_Remove(index: number): void {
        // Do not allow removal of the primary loan details (first loan in the array)
        if (index > 0 && index < this.LoanDetails.length) {
            this.LoanDetails.splice(index, 1);
        }
    } public Applicant_Add(): void {
        // Create a deep copy of the ApplicantInputData template
        const applicantInputData = JSON.parse(JSON.stringify(this.ApplicantInputData));

        // Find the type field and set it to "Individual" by default
        const typeField = applicantInputData.find((f: any) => f.Name === 'INP_ApplicantType');
        if (typeField) {
            typeField.Value = 'Individual';
        }

        // Add new applicant with default values
        this.ApplicantDetails.push({
            sectionTitle: this.ApplicantDetails.length === 0 ? 'Primary Borrower' : 'Additional Party',
            type: 'Individual',
            role: this.ApplicantDetails.length === 0 ? 'Primary Borrower' : '',
            applicantInputData: applicantInputData
        });
    }

    public Applicant_Remove(index: number): void {
        // Do not allow removing the primary borrower (first applicant in the array)
        if (index > 0 && index < this.ApplicantDetails.length) {
            this.ApplicantDetails.splice(index, 1);
        }
    } public ApplicantType_Changed(applicant: IApplicantDetail): void {
        // Update the type field in the applicant's input data
        if (applicant && applicant.applicantInputData) {
            const typeField = applicant.applicantInputData.find((f: any) => f.Name === 'INP_ApplicantType');
            if (typeField) {
                // Update the field value to match the selected type
                typeField.Value = applicant.type;
            }
        }
    }


    public Security_Add(): void {
        this.Securities.push({
            addressSearch: '',
            isInternational: false,
            unitNumber: '', levelNumber: '', streetNumber: '', streetName: '', streetType: '', suburb: '', postcode: '', state: '', country: 'Australia',
            intlAddress: ''
        });
    }

    public Security_Remove(index: number): void {
        if (index > -1 && index < this.Securities.length) {
            this.Securities.splice(index, 1);
        }
    }

    // Updates the lender value in all loan details when the global lender changes    
    public GlobalLender_Changed(): void {
        if (this.LoanDetails && this.LoanDetails.length > 0) {
            this.LoanDetails.forEach(loan => {
                // Find the lender field in each loan
                const lenderField = loan.find(field => field.Name === 'INP_Lender');

                // If lender field exists, update it
                if (lenderField !== undefined) {
                    lenderField.Value = this.selectedLender;
                } else {
                    // If field doesn't exist, add it
                    loan.push({
                        Name: "INP_Lender",
                        DisplayName: "Lender",
                        Placeholder: "",
                        HTMLInputType: "hidden",
                        Type: "string",
                        AutoCompleteControlTypeName: "",
                        CustomCSS: "",
                        IsMandatory: true,
                        Value: this.selectedLender,
                        IsInputField: false
                    });
                }
            });
        }
    }

    // Group application data by Entity type for summary display
    public SummaryEntityGroups_Get() {
        // Collect all fields from LoanDetails and ApplicantDetails
        const entityMap: { [entityType: string]: { DisplayName: string, Value: string }[] } = {};

        // Loan Details
        this.LoanDetails.forEach((loan: Array<{ Entity: string; DisplayName: string; Value: unknown; HTMLInputType: string; }>) => {
            loan.forEach((field) => {
                if (
                    typeof field.Entity === 'string' && field.Entity !== '' &&
                    typeof field.DisplayName === 'string' && field.DisplayName !== '' &&
                    typeof field.Value !== 'undefined' && field.Value !== null &&
                    field.HTMLInputType !== 'hidden'
                ) {
                    if (!(field.Entity in entityMap)) entityMap[field.Entity] = [];
                    entityMap[field.Entity].push({
                        DisplayName: field.DisplayName,
                        Value: (typeof field.Value === 'object' && field.Value !== null && 'DisplayName' in field.Value) ? (field.Value as { DisplayName: string }).DisplayName : String(field.Value)
                    });
                }
            });
        });

        // Applicant Details
        this.ApplicantDetails.forEach(applicant => {
            applicant.applicantInputData.forEach((field: { Entity: string; DisplayName: string; Value: unknown; HTMLInputType: string; }) => {
                if (
                    typeof field.Entity === 'string' && field.Entity !== '' &&
                    typeof field.DisplayName === 'string' && field.DisplayName !== '' &&
                    typeof field.Value !== 'undefined' && field.Value !== null &&
                    field.HTMLInputType !== 'hidden'
                ) {
                    if (!(field.Entity in entityMap)) entityMap[field.Entity] = [];
                    entityMap[field.Entity].push({
                        DisplayName: field.DisplayName,
                        Value: (typeof field.Value === 'object' && field.Value !== null && 'DisplayName' in field.Value) ? (field.Value as { DisplayName: string }).DisplayName : String(field.Value)
                    });
                }
            });
        });

        // Securities
        if (Array.isArray(this.Securities) && this.Securities.length > 0) {
            entityMap['Securities'] = this.Securities.map((s, i) => ({
                DisplayName: `Security ${i + 1} Address`,
                Value: s.isInternational ? s.intlAddress : `${s.unitNumber} ${s.levelNumber} ${s.streetNumber} ${s.streetName} ${s.streetType} ${s.suburb} ${s.postcode} ${s.state} ${s.country}`.replace(/ +/g, ' ').trim()
            }));
        }

        // Bank Details
        if (typeof this.BankDetails === 'object' && this.BankDetails !== null) {
            entityMap['BankDetails'] = [
                { DisplayName: 'BSB', Value: this.BankDetails.bsb },
                { DisplayName: 'Account Number', Value: this.BankDetails.accountNumber },
                { DisplayName: 'Account Name', Value: this.BankDetails.accountName }
            ];
        }

        // Convert to array for template
        return Object.keys(entityMap).map(entityType => ({
            entityType,
            items: entityMap[entityType]
        }));
    }
}

// Interfaces

interface IApplicantDetail {
    sectionTitle: string;
    type: string;
    role: string;
    applicantInputData: Array<{
        Entity: string;
        Name: string;
        DisplayName: string;
        Value: any;
        HTMLInputType: string;
        IsMandatory?: boolean;
        IsInputField?: boolean;
        Placeholder?: string;
        Type?: string;
        AutoCompleteControlTypeName?: string;
        CustomCSS?: string;
        DropdownOptions?: string;
    }>;
}

interface ISecurity {
    addressSearch: string; isInternational: boolean;
    unitNumber: string; levelNumber: string; streetNumber: string; streetName: string; streetType: string; suburb: string; postcode: string; state: string; country: string;
    intlAddress: string;
}
interface IBankDetails { bsb: string; accountNumber: string; accountName: string; }
